import { useNavigate } from "react-router-dom";
import { ChevronLeft, Printer, Flame } from "lucide-react";
import TrackerLogger from "@/components/TrackerLogger";
import { TRACKERS } from "@/lib/trackers";

const ROWS = Array.from({ length: 7 });
const LEVELS = ["Mild", "Medium", "Strong"];

export default function HotFlashDiary() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-app" data-testid="hot-flash-diary-page">
      <div className="app-frame px-5 pt-6 pb-16 print-area">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-ink-muted mb-4 no-print" data-testid="diary-back">
          <ChevronLeft size={20} /> Back
        </button>

        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-sage flex items-center justify-center mx-auto">
            <Flame size={24} className="text-brand" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-ink mt-4">Weekly Hot Flash Diary</h1>
          <p className="text-ink-soft text-sm mt-2 max-w-md mx-auto">
            Understanding your hot flash patterns helps create an effective management plan. Use this simple diary to note when they occur and what might trigger them.
          </p>
        </div>

        <div className="mt-6">
          <p className="overline-label">Track your hot flash experience</p>
          <p className="text-ink-soft text-sm mt-2">Note each episode's timing, intensity and circumstances to help identify patterns and triggers.</p>
        </div>

        <div className="mt-5 no-print">
          <TrackerLogger cfg={TRACKERS["hot-flash-diary"]} />
        </div>

        <p className="overline-label mt-8 no-print">Prefer paper?</p>
        <p className="text-ink-soft text-sm mt-1 mb-3 no-print">Print a blank copy to fill in by hand.</p>

        <div className="mt-5 rounded-2xl border-2 border-[var(--brand)] overflow-hidden">
          <div className="grid grid-cols-12 bg-sage px-3 py-3">
            <div className="col-span-4 font-semibold text-ink text-sm">When</div>
            <div className="col-span-3 font-semibold text-ink text-sm">How strong</div>
            <div className="col-span-5 font-semibold text-ink text-sm">What was happening</div>
          </div>
          {ROWS.map((_, r) => (
            <div key={r} className={`grid grid-cols-12 px-3 py-4 ${r % 2 ? "bg-app" : "bg-white"} border-t border-subtle`} data-testid={`diary-row-${r}`}>
              <div className="col-span-4 pr-2 space-y-3 text-sm text-ink-soft">
                <div>Date: <span className="inline-block border-b border-[var(--surface-accent)] w-20 sm:w-24 align-bottom" /></div>
                <div>Time: <span className="inline-block border-b border-[var(--surface-accent)] w-20 sm:w-24 align-bottom" /></div>
              </div>
              <div className="col-span-3 space-y-2">
                {LEVELS.map((lvl) => (
                  <div key={lvl} className="flex items-center gap-2 text-sm text-ink-soft">
                    <span className="w-4 h-4 rounded border-2 border-[var(--brand)] flex-shrink-0" />
                    {lvl}
                  </div>
                ))}
              </div>
              <div className="col-span-5">
                <div className="h-full min-h-[72px] rounded-lg border border-[var(--surface-accent)]" />
              </div>
            </div>
          ))}
        </div>

        <button
          data-testid="diary-print"
          onClick={() => window.print()}
          className="mt-6 w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 no-print transition-colors"
        >
          <Printer size={16} /> Print / Download diary
        </button>
        <p className="text-ink-muted text-xs mt-3 text-center no-print">A self tracking tool for your own notes, not a medical record.</p>
      </div>
    </div>
  );
}
